import type { QueryClient } from '@tanstack/react-query'
import { TipoEnergia, TipoVeiculo, TIPO_ENERGIA_LABEL } from '../../lib/enums'
import type { ValorFipe } from '../../types'

export const iconeVeiculo = (tipo: TipoVeiculo, energia: TipoEnergia) =>
  tipo === TipoVeiculo.Moto ? 'ri-motorbike-line' : energia === TipoEnergia.Eletrico ? 'ri-charging-pile-2-line' : 'ri-car-line'

export const formatKm = (km: number) => `${new Intl.NumberFormat('pt-BR').format(km)} km`

/** yyyy-MM -> MM/yyyy */
export const mesReferencia = (m: string) => { const [a, mm] = m.split('-'); return mm && a ? `${mm}/${a}` : m }

export const energiaLabel = (e: TipoEnergia) => TIPO_ENERGIA_LABEL[e] ?? ''

export const usaEnergiaEletrica = (e: TipoEnergia) => e === TipoEnergia.Eletrico || e === TipoEnergia.HibridoPlugin

export const fonteFipe = (v: ValorFipe) => v.fonte || `Fonte: Tabela FIPE, referência ${mesReferencia(v.mesReferencia)}. Não é avaliação oficial.`

export const hojeIso = () => new Date().toISOString().slice(0, 10)

export const numeroBr = (n: number, casas = 2) => new Intl.NumberFormat('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas }).format(n)

/** Valor digitado (aceita vírgula) -> número, ou undefined se vazio. */
export const lerNumero = (s: string) => (s.trim() === '' ? undefined : Number(s.replace(',', '.')))

/** Primeiro dia do mês corrente em yyyy-MM-dd. */
export const inicioDoMes = () => `${hojeIso().slice(0, 7)}-01`

/** Datas vêm como ISO; o input date quer yyyy-MM-dd. */
export const soData = (iso: string) => iso.slice(0, 10)

export const unidadeConsumo = (u: number) => (u === 2 ? 'km/kWh' : u === 3 ? 'km/m³' : 'km/l')

/** Invalida o que o lançamento ligado mexe: veículos, transações, contas e cartões. */
export function invalidarVeiculo(qc: QueryClient) {
  for (const k of ['veiculos', 'transacoes', 'contas', 'cartoes', 'cartoes-utilizaveis', 'faturas', 'dashboard']) qc.invalidateQueries({ queryKey: [k] })
}

/** Prévia do que o abastecimento faz no cálculo de consumo. */
export function previaAbastecimento(cheio: boolean, reserva: boolean): { titulo: string; texto: string; tom: string } {
  if (cheio && reserva) return { titulo: 'Fecha trecho cheio a cheio', texto: 'Serve como âncora de tanque cheio e de reserva. Quando os dois cálculos são possíveis, vale o cheio a cheio.', tom: 'text-income' }
  if (cheio) return { titulo: 'Fecha trecho cheio a cheio', texto: 'O consumo é calculado entre este e o abastecimento anterior com tanque cheio.', tom: 'text-income' }
  if (reserva) return { titulo: 'Fecha trecho reserva a reserva (estimativa)', texto: 'O consumo é uma estimativa entre duas vezes em que o tanque estava na reserva.', tom: 'text-warning' }
  return { titulo: 'Abastecimento parcial', texto: 'Não fecha trecho agora, mas os litros entram na conta do próximo trecho fechado.', tom: 'text-info' }
}

/** Cor do status de manutenção/vencimento: Em dia, Perto de vencer, Vencido. */
export const toneStatus = (s: number): 'income' | 'warning' | 'expense' => (s === 3 ? 'expense' : s === 2 ? 'warning' : 'income')
